// B3 — Cohesion + Squad Mode (pure logic, no React).
// Grounded in the local rules: cr-cohesion-cohesion, cr-cohesion-cohesion-damage,
// cr-squad-mode-maintaining-squad-mode, cr-squad-mode-squad-mode-abilities.
//
//   Cohesion measures how well the Kill-team acts as one unit. It is used to
//   enter, maintain and power Squad Mode abilities. It is lost through
//   casualties, fear, separation, failed leadership, or mission complications;
//   the leader's Command/WP tests and regrouping can resist or restore it.
//   If Cohesion reaches 0, Squad Mode can no longer be maintained.
//
// All functions are pure: they return a NEW state object and never mutate the
// input. React wraps these in a useState; tests drive them directly.

export const SQUAD_MAX = 10
export const SQUAD_START = 5

// Fear tiers: a failed fear roll maps to a "fear level" (1-3) which sets how
// much Cohesion the squad loses. Higher fear (worse margin) = more loss.
export function fearLevelFor(failure) {
  // failure = DoF from the fear test (0 = not a fear event)
  const dof = Math.max(0, failure)
  if (dof <= 0) return 0
  if (dof <= 2) return 1
  if (dof <= 4) return 2
  return 3
}

// How much Cohesion a fear level removes.
export function fearCohesionLoss(level) {
  if (level <= 0) return 0
  if (level >= 3) return 3
  if (level === 2) return 2
  return 1
}

// A squad-leadership (Command / Willpower) test resists some Cohesion loss.
// Full success (DoS>=3) resists everything; a hit (DoS 1-2) resists half
// (rounded down); a miss resists nothing.
export function leadershipResist(loss, success) {
  const dos = Math.max(0, success)
  if (dos <= 0) return 0
  if (dos >= 3) return loss
  return Math.floor(loss / 2)
}

// Regrouping restores Cohesion, scaled by the DoS of the leadership test.
export function regroupGain(success) {
  const dos = Math.max(0, success)
  if (dos <= 0) return 0
  if (dos >= 3) return 2
  return 1
}

function clampCohesion(v) {
  return Math.max(0, Math.min(SQUAD_MAX, Math.trunc(v)))
}

function withEvents(state, events) {
  return { ...state, events: [...(state.events || []), ...events] }
}

// Squad Mode is maintainable only while Cohesion > 0.
export function canMaintainSquadMode(state) {
  return state.cohesion > 0
}

export function newSquadState({ cohesion = SQUAD_START, inSquadMode = true, leader = 'Squad Leader', events = [] } = {}) {
  const cohesionC = clampCohesion(cohesion)
  return {
    cohesion: cohesionC,
    inSquadMode: cohesionC > 0 ? inSquadMode : false,
    leader,
    events,
  }
}

// Reduce Cohesion by `amount` (reason for the log). When it hits 0 and Squad
// Mode was active, Squad Mode is broken and we report that.
export function applyCohesionDamage(state, amount, reason) {
  const loss = Math.max(0, Math.trunc(amount))
  if (loss === 0) return { state, lostSquadMode: false }
  const before = state.cohesion
  const after = clampCohesion(before - loss)
  const broke = state.inSquadMode && after === 0
  let next = {
    ...state,
    cohesion: after,
    inSquadMode: after > 0 ? state.inSquadMode : false,
  }
  const evs = [{ type: 'damage', amount: loss, reason, cohesion: after }]
  if (broke) evs.push({ type: 'squadModeLost', reason, cohesion: after })
  next = withEvents(next, evs)
  return { state: next, lostSquadMode: broke }
}

// A fear event: compute the level from the fear test's DoF, then apply the
// matching Cohesion loss (optionally reduced by a leadership resist).
export function applyFear(state, fearDof, { leaderDos = 0 } = {}) {
  const level = fearLevelFor(fearDof)
  const raw = fearCohesionLoss(level)
  if (raw === 0) return { state, level: 0, loss: 0, led: false }
  const resisted = leadershipResist(raw, leaderDos)
  const loss = raw - resisted
  const res = applyCohesionDamage(state, loss, `fear (level ${level}${resisted ? ', leader resisted ' + resisted : ''})`)
  return { state: res.state, level, loss, led: leaderDos > 0 }
}

// A leadership / regrouping test that restores Cohesion.
export function applyRegroup(state, dos) {
  const gain = regroupGain(dos)
  if (gain === 0) return { state, gain: 0 }
  const next = { ...state, cohesion: clampCohesion(state.cohesion + gain), inSquadMode: true }
  return { state: withEvents(next, [{ type: 'regroup', gain, reason: 'regroup', cohesion: next.cohesion }]), gain }
}

// Squad Mode abilities spend Cohesion. Each has a cost and a scope. A member
// outside Squad Mode (or a Cohesion pool below the cost) cannot use one.
export const SQUAD_ABILITIES = [
  { id: 'suppression', name: 'Suppressing Volley', cost: 1, scope: 'team', blurb: 'The squad lays down coordinated covering fire.' },
  { id: 'line', name: 'Defensive Line', cost: 2, scope: 'team', blurb: 'Holding formation — the team is harder to break.' },
  { id: 'salvo', name: 'Coordinated Salvo', cost: 1, scope: 'team', blurb: 'A combined strike, all weapons on one target.' },
]

export function canUseAbility(state, ability) {
  return !!state.inSquadMode && state.cohesion >= (ability.cost || 1)
}

export function useAbility(state, ability) {
  if (!canUseAbility(state, ability)) {
    const reason = state.inSquadMode ? 'not enough Cohesion' : 'not in Squad Mode'
    return { state: withEvents(state, [{ type: 'abilityBlocked', reason: ability.name + ': ' + reason }]), used: false }
  }
  const cost = ability.cost || 1
  const after = clampCohesion(state.cohesion - cost)
  const broke = state.inSquadMode && after === 0
  let next = {
    ...state,
    cohesion: after,
    inSquadMode: after > 0 ? state.inSquadMode : false,
  }
  const evs = [{ type: 'ability', ability: ability.name, cost, cohesion: after }]
  if (broke) evs.push({ type: 'squadModeLost', reason: ability.name + ' drained Cohesion to 0' })
  next = withEvents(next, evs)
  return { state: next, used: true, broke }
}

// A defeated/dropped-out Battle-Brother hurts the team's Cohesion.
export function casualtyDamage(state, name, { leaderDos = 0 } = {}) {
  const raw = 2
  const resisted = leadershipResist(raw, leaderDos)
  const loss = raw - resisted
  const res = applyCohesionDamage(state, loss, `casualty: ${name}${resisted ? ' (leader resisted ' + resisted + ')' : ''}`)
  return { state: res.state, loss, led: leaderDos > 0 }
}

export default SQUAD_ABILITIES
